// REACT IMPORTS
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
// COMPONENT IMPORTS
import Button from "components/form/Button";
import { useAppSelector } from "store/hooks";
import { formatContent, replaceMarkers } from "utils/HelperFunctions";
import QuestionsFooter from "./QuestionsFooter";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { RootState } from "store/store";
import { useTid } from "utils/testid";
import { SettingsValue } from "types/payments";
// PROPS TYPE
interface LegalNoticeProps {
  onClick: () => void;
}

/* ------------------------------------- LEGAL NOTICE ------------------------------------- */

const LegalNotice = (props: LegalNoticeProps) => {
  const tid = useTid();
  const { onClick } = props;
  const { companyDetails, companyMarkers } = useAppSelector(
    (state: RootState) => state.payment
  );
  const portalSettings = usePortalSettingsData();
  const [legalNoticeText, setLegalNoticeText] = useState<string>("");

  const shouldShowLegalNotice =
    (portalSettings?.legal_notice_option === SettingsValue.CUSTOM ||
      portalSettings?.legal_notice_option === SettingsValue.DEFAULT) &&
    Boolean(portalSettings?.legal_notice);

  useEffect(() => {
    if (shouldShowLegalNotice) {
      const sanitizedHtml = replaceMarkers(
        portalSettings?.legal_notice || "",
        companyMarkers
      );

      const formattedHtml = formatContent(sanitizedHtml);

      setLegalNoticeText(formattedHtml);
    } else {
      setLegalNoticeText("");
    }
  }, [companyMarkers, portalSettings?.legal_notice, shouldShowLegalNotice]);

  return (
    <div
      className="w-full h-full animate__animated animate__fadeIn flex flex-col justify-between items-center"
      data-testid={tid("legal-notice")}
      aria-label="Legal notice"
    >
      <div>
        <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
          Legal Notice
        </div>
        {shouldShowLegalNotice ? (
          <div
            id="legal-notice-content"
            data-testid={tid("legal-notice-content")}
            className="legal-notice-content pt-6 lg:pt-10 max-w-[520px] text-txtParagraph lg:text-base text-sm leading-normal font-normal"
            dangerouslySetInnerHTML={{ __html: legalNoticeText || "" }}
          ></div>
        ) : (
          <>
            <p className="pt-6 lg:pt-10 max-w-[520px] text-txtParagraph lg:text-base text-sm leading-normal font-normal">
              Welcome to{" "}
              {(companyDetails?.name || companyDetails?.company_name) ??
                "[Company Name]"}{" "}
              Customer Portal. By using our services, you agree to comply with
              our&nbsp;
              <Link
                to="https://www.dcdial.com/legal"
                target="_blank"
                rel="noreferrer"
                className="text-blue hover:!text-btnPrimaryHover"
                data-testid={tid("intro-terms-of-service-link")}
                aria-label="Terms of service"
              >
                terms of service
              </Link>
              , which govern your use of this portal. Please review the&nbsp;
              <Link
                to="https://www.dcdial.com/legal"
                target="_blank"
                rel="noreferrer"
                className="text-blue hover:!text-btnPrimaryHover"
                data-testid={tid("review-terms-of-service-link")}
                aria-label="Terms of service"
              >
                terms of service
              </Link>
              &nbsp; carefully, as they contain important information regarding
              your rights and responsibilities when using our services.
            </p>
            <p className="lg:pt-5 pt-4 max-w-[520px] text-txtParagraph lg:text-base text-sm leading-normal font-normal">
              Please use this portal responsibly, adhering to all applicable
              laws. If you have any questions or concerns about our&nbsp;
              <Link
                to="https://www.dcdial.com/legal"
                target="_blank"
                rel="noreferrer"
                className="text-blue hover:!text-btnPrimaryHover"
                data-testid={tid("contact-terms-of-service-link")}
                aria-label="Terms of service"
              >
                terms of service
              </Link>
              &nbsp; or this Customer Portal, please contact us. Your use of our
              portal constitutes your acceptance of the&nbsp;
              <Link
                to="https://www.dcdial.com/legal"
                target="_blank"
                rel="noreferrer"
                className="text-blue hover:!text-btnPrimaryHover"
                data-testid={tid("acceptance-terms-of-service-link")}
                aria-label="Terms of service"
              >
                terms of service
              </Link>
              .
            </p>
          </>
        )}
        <div className="lg:pt-[60px] pt-10">
          <Button
            text="Accept"
            className="w-full max-w-[520px]"
            onClick={onClick}
            variant="variable"
            data-testid={tid("accept-legal-notice-button")}
            aria-label="Accept legal notice"
          />
        </div>
      </div>
      <div className={`pb-5 lg:pt-0 pt-4`}>
        <QuestionsFooter />
      </div>
    </div>
  );
};

export default LegalNotice;
